import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  for: "bucket-name",
  class: "fw-bold"
}
const _hoisted_2 = {
  for: "bucket-friendly-name",
  class: "fw-bold"
}
const _hoisted_3 = {
  for: "bucket-description",
  class: "fw-bold"
}
const _hoisted_4 = {
  for: "bucket-type",
  class: "fw-bold"
}
const _hoisted_5 = {
  for: "bucket-expiration",
  class: "fw-bold"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = {
  for: "bucket-labels",
  class: "fw-bold"
}
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { key: 2 }

import { computed, ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { BucketCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { BUCKET_DATA_TYPES } from '@/constants';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useUserStore } from '@/store/user';


export default /*@__PURE__*/_defineComponent({
  __name: 'BucketCreate',
  props: {
    show: { type: Boolean },
    defaultDataType: {}
  },
  emits: ['success', 'close'],
  setup(__props: any, { emit: __emit }) {

const userStore = useUserStore();

// --- Component properties ---
const props = __props;

// --- Component emits ---
const emits = __emit;

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const bucketName = ref<string>('');
const bucketFriendlyName = ref<string>('');
const description = ref<string>('');
const dataType = computed<string>(() => {
  return props.defaultDataType || BUCKET_DATA_TYPES.INPUT;
});
const expirationDays = ref<number>(30);
const labels = ref<string>(''); // Comma-separated labels input
const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketCreate) {
    console.debug('Bucket created:', res.data.bucketCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('success');
    }, 1000);
  } else {
    mutationError.value = 'Dataset creation failed: ' + res.data;
    console.error('Bucket creation failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting bucket:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onMutationConfirmed() {
  mutationInProgress.value = true;
  mutationError.value = null;

  executeMutation({
    friendlyName: bucketFriendlyName.value,
    name: bucketName.value ? bucketName.value : undefined,
    description: description.value,
    dataType: dataType.value,
    region: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
    expiresInDays: expirationDays.value > 0 ? expirationDays.value : undefined,
    labels: labels.value
      ? labels.value
          .split(';')
          .map((label) => label.trim())
          .filter((label) => label.length > 0)
      : undefined,
  });
}

function handleCloseModal() {
  emits('close');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((show).value = $event)),
      onHide: handleCloseModal
    }, {
      title: _withCtx(() => [
        _createVNode(_unref(FontAwesomeIcon), {
          icon: "database",
          class: "me-2"
        }),
        _cache[11] || (_cache[11] = _createTextVNode(" Create New Dataset ", -1))
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: handleCloseModal
        }, {
          default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(BButton), {
          variant: "outline-primary",
          disabled: 
            mutationInProgress.value || mutationShowSuccess.value || !bucketFriendlyName.value
          ,
          onClick: onMutationConfirmed
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), {
              icon: "plus",
              class: "me-1"
            }),
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                  _cache[24] || (_cache[24] = _createTextVNode(" Creating Dataset ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_9, [
                    _cache[25] || (_cache[25] = _createTextVNode(" Dataset Created ", -1)),
                    _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'check-double'] })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_10, " Create Dataset "))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _cache[22] || (_cache[22] = _createElementVNode("p", null, [
            _createTextVNode(" This is a developer-only feature for creating new empty datasets. If you are developer or administrator, use this to create a new empty storage bucket. Then you can extract the bucket ID and upload data directly from your dev machine, GCE, AWS or TrueNAS using CLI tools such as "),
            _createElementVNode("i", null, "gsutil"),
            _createTextVNode(". ")
          ], -1)),
          (_unref(userStore).isAdmin)
            ? (_openBlock(), _createBlock(_unref(BFormGroup), { key: 0 }, {
                default: _withCtx(() => [
                  _createElementVNode("label", _hoisted_1, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "tag",
                      class: "me-1"
                    }),
                    _cache[12] || (_cache[12] = _createTextVNode(" Bucket name ", -1)),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "shield-halved",
                      class: "ms-1 text-muted",
                      id: "icon-admin-bucket-name"
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    modelValue: bucketName.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((bucketName).value = $event)),
                    required: "",
                    id: "bucket-name",
                    type: "text",
                    placeholder: "Leave empty for auto-generated name",
                    disabled: mutationInProgress.value || mutationShowSuccess.value
                  }, null, 8, ["modelValue", "disabled"]),
                  _createVNode(_unref(BTooltip), {
                    target: "icon-admin-bucket-name",
                    triggers: "hover focus"
                  }, {
                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                      _createTextVNode(" Optional unique bucket name. If left empty, a unique name will be generated. The bucket name can only contain lowercase letters, numbers, and hyphens, and must start with a letter. ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createElementVNode("label", _hoisted_2, [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "tag",
                  class: "me-1"
                }),
                _cache[14] || (_cache[14] = _createTextVNode(" Dataset Name ", -1))
              ]),
              _createVNode(_unref(BFormInput), {
                modelValue: bucketFriendlyName.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((bucketFriendlyName).value = $event)),
                required: "",
                id: "bucket-friendly-name",
                type: "text",
                placeholder: "Type in dataset name",
                disabled: mutationInProgress.value || mutationShowSuccess.value
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createElementVNode("label", _hoisted_3, [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "align-left",
                  class: "me-1"
                }),
                _cache[15] || (_cache[15] = _createTextVNode(" Description ", -1))
              ]),
              _createVNode(_unref(BFormTextarea), {
                id: "bucket-description",
                modelValue: description.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((description).value = $event)),
                placeholder: "Type in dataset description",
                disabled: mutationInProgress.value || mutationShowSuccess.value
              }, null, 8, ["modelValue", "disabled"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createElementVNode("label", _hoisted_4, [
                _createVNode(_unref(FontAwesomeIcon), {
                  icon: "database",
                  class: "me-1"
                }),
                _cache[16] || (_cache[16] = _createTextVNode(" Dataset Type ", -1))
              ]),
              _createVNode(_unref(BFormSelect), {
                id: "bucket-type",
                modelValue: dataType.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dataType).value = $event)),
                options: [
              { value: _unref(BUCKET_DATA_TYPES).INPUT, text: 'input' },
              { value: _unref(BUCKET_DATA_TYPES).OUTPUT, text: 'output' },
              ...(_unref(userStore).isAdmin
                ? [{ value: _unref(BUCKET_DATA_TYPES).SYSTEM, text: 'system' }]
                : []),
            ],
                disabled: mutationInProgress.value || mutationShowSuccess.value
              }, null, 8, ["modelValue", "options", "disabled"])
            ]),
            _: 1
          }),
          (_unref(userStore).isAdmin)
            ? (_openBlock(), _createBlock(_unref(BFormGroup), {
                key: 1,
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("label", _hoisted_5, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "clock",
                      class: "me-1"
                    }),
                    _cache[17] || (_cache[17] = _createTextVNode(" Expiration in days ", -1)),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "shield-halved",
                      class: "ms-1 text-muted",
                      id: "icon-admin-expiration"
                    }),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "circle-question",
                      class: "ms-1 text-muted",
                      id: "icon-help-expiration"
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    id: "bucket-expiration",
                    type: "number",
                    min: _unref(userStore).isAdmin ? 0 : 1,
                    max: _unref(userStore).isAdmin ? 365 : 30,
                    modelValue: expirationDays.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((expirationDays).value = $event)),
                    disabled: mutationInProgress.value || mutationShowSuccess.value
                  }, null, 8, ["min", "max", "modelValue", "disabled"]),
                  _createVNode(_unref(BTooltip), {
                    target: "icon-admin-expiration",
                    triggers: "hover focus"
                  }, {
                    default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                      _createTextVNode(" Admins can set expiration to 0 for no expiration or up to 365 days. For non-admins, the value is set to 30 days automatically. ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_unref(BTooltip), {
                    target: "icon-help-expiration",
                    triggers: "hover focus"
                  }, {
                    default: _withCtx(() => [
                      _cache[19] || (_cache[19] = _createTextVNode(" Number of days until the dataset expires. After expiration, the dataset will be automatically deleted. ", -1)),
                      (_unref(userStore).isAdmin)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_6, "Set to 0 for no expiration."))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_unref(userStore).isAdmin)
            ? (_openBlock(), _createBlock(_unref(BFormGroup), {
                key: 2,
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("label", _hoisted_7, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "layer-group",
                      class: "me-1"
                    }),
                    _cache[20] || (_cache[20] = _createTextVNode(" Labels ", -1)),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "shield-halved",
                      class: "ms-1 text-muted",
                      id: "icon-admin-labels"
                    }),
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "circle-question",
                      class: "text-muted ms-1",
                      id: "icon-help-labels"
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    id: "bucket-labels",
                    type: "text",
                    placeholder: "Semicolon-separated labels",
                    modelValue: labels.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((labels).value = $event)),
                    disabled: mutationInProgress.value || mutationShowSuccess.value
                  }, null, 8, ["modelValue", "disabled"]),
                  _createVNode(_unref(BTooltip), {
                    target: "icon-help-labels",
                    triggers: "hover focus"
                  }, {
                    default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                      _createTextVNode(" Optional labels to help organize and filter datasets. Enter as semicolon-separated strings in `key` or `key:value` format (e.g. 'watchdog-exempt;integration-tests:true;length:255'). ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(CloudRegionSelection, {
            modelValue: customCloudRegionPrefix.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((customCloudRegionPrefix).value = $event)),
            modelRequired: customCloudRegionRequired.value,
            "onUpdate:modelRequired": _cache[7] || (_cache[7] = ($event: any) => ((customCloudRegionRequired).value = $event)),
            class: "mt-3"
          }, null, 8, ["modelValue", "modelRequired"]),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[9] || (_cache[9] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})